import {
  EnergyLabelsRequestParams,
  EnergyLabelsResponse,
} from "@/components/EnergySaving/data/EnergyLabelsFetcher.ts";
import { Variant } from "@/types/types.ts";
import {
  EnergyLabelMetaAverageUsage,
  EnergyLabelProductFoundResult,
} from "@/components/EnergySaving/data/energyLabelResponseSchema.ts";
import { UsageControlParams } from "@/components/EnergySaving/components/EnergySavingModal/EnergySavingModal.tsx";
import { EnergyLabelInputValues } from "@/components/EnergySaving/EnergySavingUI.tsx";

export enum Rating {
  BEST = "best",
  GOLD = "gold",
  SILVER = "silver",
  BRONZE = "bronze",
  GOODVALUE = "good value",
}

export const energyDescriptionMap: { [index: string]: string } = {
  [Rating.BEST]: "Best for energy savings",
  [Rating.GOLD]: "Gold for energy savings",
  [Rating.SILVER]: "Silver for energy savings",
  [Rating.BRONZE]: "Bronze for energy savings",
  [Rating.GOODVALUE]: "Good price vs efficiency",
};

export const RATING_NOT_POSSIBLE = "Rating not possible";

export const imageBaseUrl =
  "https://static.johnlewiscontent.com/v1/static/bronze-button-v2/Large_Elec_Icons";
export const FALLBACK_MEDAL_URL = `${imageBaseUrl}/none-button-v2.svg`;

export const ratingMedalMap: { [index: string]: string } = {
  [Rating.BEST]: `${imageBaseUrl}/platinum-button-v2%20(1).svg`,
  [Rating.GOLD]: `${imageBaseUrl}/gold-button-v2.svg`,
  [Rating.SILVER]: `${imageBaseUrl}/silver-button-v2.svg`,
  [Rating.BRONZE]: `${imageBaseUrl}/bronze-button-v2.svg`,
  [Rating.GOODVALUE]: `${imageBaseUrl}/plain-button-v2.svg`,
};

export const getEnergyLabelResult = (
  energyLabels: EnergyLabelsResponse,
  variant: Variant,
): EnergyLabelProductFoundResult | null => {
  if (energyLabels.error || !energyLabels?.results) return null;

  const result = energyLabels.results[variant.title];

  if (!result) return null;

  return result as EnergyLabelProductFoundResult;
};

export const getTariff = (energyLabelsResponse: EnergyLabelsResponse) => {
  const metadata = energyLabelsResponse.responseMetaData;
  if (!metadata) return null;
  const {
    general: { tariff },
  } = metadata;

  return tariff;
};

export const getAverageUsage = (
  sellingProductType: string,
  energyLabelsResponse: EnergyLabelsResponse,
): EnergyLabelMetaAverageUsage | null => {
  const metadata = energyLabelsResponse.responseMetaData;
  const productType = getProductType(sellingProductType);

  if (!metadata || !productType) return null;

  const {
    general: { productTypes },
  } = metadata;

  if (Array.isArray(productTypes) || !productTypes[productType]) return null;

  const {
    averageUsage,
    averageUsageTimesPerPeriod,
    averageUsagePeriod,
    energyCostYears,
  } = productTypes[productType];

  return {
    averageUsage,
    averageUsageTimesPerPeriod,
    averageUsagePeriod,
    energyCostYears,
  };
};

export const getProductType: (
  sellingProductType: string | undefined,
) => string | undefined = (sellingProductType: string | undefined) => {
  if (!sellingProductType) return;

  return {
    "Fridge Freezers": "fridge-freezers",
    Freezers: "freezers",
    Fridges: "fridges",
    "Washing Machines": "washing-machines",
    "Washer Dryers": "washer-dryers",
    "Tumble Dryers": "tumble-dryers",
    Dishwashers: "dishwashers",
  }[sellingProductType];
};

export const isFridgeType = (productType: string) => {
  return ["fridge-freezers", "freezers", "fridges"].includes(productType);
};

const formatOptions = (values: Array<number | string>) =>
  values.map((value) => ({
    label: value,
    value,
  }));

const yearValues = new Array<number>(20).fill(0).map((_, index) => index + 1);
export const yearOptions = formatOptions(yearValues).map((option) => ({
  label: `${option.value} ${option.value === 1 ? "year" : "years"}`,
  value: option.value,
}));

const usageValues = [1, 2, 3, 4, 5, 5.5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
export const usageOptions = formatOptions(usageValues);

export const periodMap: { [key: string]: number } = {
  month: 12,
  week: 52,
  day: 365,
};
export const periodOptions = Object.keys(periodMap).map((label) => {
  return {
    label,
    value: periodMap[label] || 0,
  };
});

export const updateRequestParams = (
  requestParams: EnergyLabelsRequestParams,
  controlValues: UsageControlParams,
  paramName: string,
  value: number,
) => {
  switch (paramName) {
    case "customEnergyCostYears":
    case "customTariff":
      requestParams[paramName] = value;
      break;
    case "customUsageTimesPerPeriod":
      if (!controlValues.customUsagePeriod) break;
      requestParams.customCyclesPerYear =
        value * controlValues.customUsagePeriod;
      break;
    case "customUsagePeriod":
      if (!controlValues.customUsageTimesPerPeriod) break;
      requestParams.customCyclesPerYear =
        value * controlValues.customUsageTimesPerPeriod;
      break;

    default:
      break;
  }

  return requestParams;
};

export const mapRequestParams = (
  controlValues: EnergyLabelInputValues | null,
) => {
  if (!controlValues) return null;

  const {
    customUsageTimesPerPeriod,
    customUsagePeriod,
    customEnergyCostYears,
    customTariff,
  } = controlValues;
  const customCyclesPerYear =
    customUsageTimesPerPeriod && customUsagePeriod
      ? customUsageTimesPerPeriod * customUsagePeriod
      : 0;

  return {
    customCyclesPerYear,
    customEnergyCostYears,
    customTariff,
  } as EnergyLabelsRequestParams;
};
